<script setup lang="ts">
import { cn } from "@/lib/utils";
import { buildTemplateContext, useTemplating } from "#bb/composables/useTemplating";
import { eventBus } from "#bb/lib/eventBus";
import {
  normalizeImageFit,
  normalizeImageFocalPosition,
  resolveImageAspectRatio,
} from "#bb/lib/image";
import { resolveSiteFileDownloadUrl } from "#bb/lib/pathContract";

const logger = useLogger("Image");
const props = defineProps<{ id: string }>();

const config = useRuntimeConfig();
const { siteId } = useSite();
const features = inject<{
  resourceDefinitionPath?: string | null;
}>("render-features", {});

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id) || {});

const report = inject<any | null>("report", null);
const templateContext = computed(() => buildTemplateContext(report));
const { applyTemplateObject } = useTemplating(templateContext, { logger });

const templated = computed(() => applyTemplateObject({
  source: definitionData.value.source || "",
  alt: definitionData.value.alt || "",
}));
const imageSource = computed(() => resolveSiteFileDownloadUrl(
  templated.value.source,
  config.public.apiPublicUrl || config.public.apiUrl,
  siteId.value,
  features.resourceDefinitionPath,
));
const isDecorative = computed(() => definitionData.value.decorative === true);
const alternativeText = computed(() =>
  isDecorative.value ? "" : String(templated.value.alt || "").trim(),
);
const aspectRatio = computed(() => resolveImageAspectRatio(
  definitionData.value.aspectRatio,
  definitionData.value.customRatioWidth,
  definitionData.value.customRatioHeight,
));
const fit = computed(() => normalizeImageFit(definitionData.value.fit));
const focalX = computed(() =>
  normalizeImageFocalPosition(definitionData.value.focalX),
);
const focalY = computed(() =>
  normalizeImageFocalPosition(definitionData.value.focalY),
);
const frameStyle = computed(() => ({
  aspectRatio: aspectRatio.value,
}));
const imageStyle = computed(() => ({
  objectFit: fit.value,
  objectPosition: `${focalX.value}% ${focalY.value}%`,
}));

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Image editor",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the image settings",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Image",
    icon: "lucide:image",
  },
});
</script>

<template>
  <figure
    :class="cn('w-full overflow-hidden rounded-lg bg-muted', $attrs.class ?? '')"
    :style="frameStyle"
  >
    <img
      v-if="imageSource"
      :src="imageSource"
      :alt="alternativeText"
      :aria-hidden="isDecorative ? 'true' : undefined"
      class="size-full"
      :style="imageStyle"
      loading="lazy"
      decoding="async"
    />

    <div
      v-else
      class="flex size-full items-center justify-center border border-dashed px-6 text-center"
      role="status"
    >
      <div class="space-y-2 text-muted-foreground">
        <Icon name="lucide:image-off" class="mx-auto size-8" />
        <p class="text-sm font-medium text-foreground">Image unavailable</p>
        <p class="text-xs">Choose a site image or enter an image URL in the block editor.</p>
      </div>
    </div>
  </figure>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

export default {
  componentId: ComponentDefinitionIds.IMAGE,
  editor: () => import("#bb/components/editor/media/image.vue"),
  icon: "lucide:image",
  type: "Image",
  description: "Display a responsive image with controlled cropping and aspect ratio.",
  default: {
    source: "",
    alt: "",
    decorative: false,
    aspectRatio: "16:9",
    customRatioWidth: 16,
    customRatioHeight: 9,
    fit: "cover",
    focalX: 50,
    focalY: 50,
  },
  categories: ["Media"],
  order: 13051,
};
</script>
